import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import {
  ClientAssociatedProfileDTO,
  useClientAssociatedProfilesQuery,
} from '../../../../TopNav/api/useClientAssociatedProfilesQuery';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { useClientAssociatedProfilesMutation } from '../../../../TopNav/api/useClientAssociatedProfilesMutation';
import { identifyFullstoryUser } from '../../../../CommonUtils/initFullstory';
import { MESSAGES } from '../../../../CommonConstants';
import { toast } from '../../../../CommonComponents/OPL/Toast/Toast';
import Spinner from '../../../../CommonComponents/Spinner';
import { selectShowRelatedHouseholds } from '../../../../CommonSelectors/CommonAreaSelector';
import { ShowRelatedHouseholdsOption } from '../../../../CommonSelectors/CommonArea.types';
import { AccordionButton } from '../../../../CommonComponents/OPL/Accordion/AccordionButton/AccordionButton';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { AccordionPanel } from '../../../../CommonComponents/OPL/Accordion/AccordionPanel/AccordionPanel';
import { AccountButton } from '../AccountGroup/AccountGroup.styles';
import { TruncatedText } from '../../../../CommonComponents/OPL/Text/TruncatedText';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';

const buttonId = `related-households--group-button`;
const panelId = `related-households--panel`;

interface Props {
  clientId: string;
}

export const RelatedHouseholds = ({ clientId }: Props) => {
  const showRelatedHouseholds = useSelector(selectShowRelatedHouseholds);

  if (showRelatedHouseholds === ShowRelatedHouseholdsOption.Hide) {
    return null;
  }

  return (
    <RelatedHouseholdsContent
      clientId={clientId}
      showInTotal={
        showRelatedHouseholds === ShowRelatedHouseholdsOption.ShowAndIncludeInAllAccounts
      }
    />
  );
};

interface ContentProps {
  clientId: string;
  showInTotal: boolean;
}

export const RelatedHouseholdsContent = ({ clientId, showInTotal }: ContentProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data } = useClientAssociatedProfilesQuery({
    clientId,
    includeValue: true,
  });

  const {
    mutateAsync: switchProfile,
    isLoading: isSwitchingProfiles,
    isSuccess: isSwitchProfilesSuccess,
  } = useClientAssociatedProfilesMutation();

  if (!data || data.length <= 1) {
    return null;
  }

  const relatedHouseholds = data.filter(({ Selected }) => showInTotal || !Selected);

  const relatedHouseholdsTotal = relatedHouseholds.reduce(
    (acc, { Value }) => acc + (Value || 0),
    0,
  );

  const handleSwitchProfile = async (household: ClientAssociatedProfileDTO) => {
    try {
      const { redirectUrl } = await switchProfile({
        userDetailId: household.Id.toString(),
        clientId,
      });

      identifyFullstoryUser(clientId, { householdId: household.Id.toString() });

      window.location.href = redirectUrl;
    } catch (error) {
      toast.error(MESSAGES.GENERIC_ERROR);

      console.error(error);
    }
  };

  return (
    <Box py={20}>
      {(isSwitchingProfiles || isSwitchProfilesSuccess) && <Spinner />}

      <AccordionButton
        variant="embedded"
        id={buttonId}
        panelId={panelId}
        isActive={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
        content={
          <Box display="flex" alignItems="center" width="100%" css="grid-gap: 8px" py="12">
            <Icon className="far fa-home" color="neutral.400" />
            <Text fontWeight={600}>Related Households</Text>
            <Text variant="tabular" fontWeight={600} ml="auto">
              {formatCurrency(relatedHouseholdsTotal, { mantissa: 2 })}
            </Text>
          </Box>
        }
        css={`
          border-top: 1px solid ${({ theme }) => theme.colors.neutral[200]};
          border-bottom: 1px solid ${({ theme }) => theme.colors.neutral[200]};
        `}
      />

      <AccordionPanel id={panelId} buttonId={buttonId} isActive={isExpanded} variant="embedded">
        {relatedHouseholds.map(household => {
          return (
            <AccountButton
              key={household.Name}
              isSelected={household.Selected}
              onClick={() => {
                if (household.Selected) {
                  return;
                }

                handleSwitchProfile(household);
              }}
            >
              <TruncatedText textAlign="left" fontWeight={500}>
                {household.Name}
              </TruncatedText>

              <Text variant="tabular" fontWeight={600} fontSize="small" ml="auto">
                {formatCurrency(household.Value || 0, { mantissa: 2 })}
              </Text>
            </AccountButton>
          );
        })}
      </AccordionPanel>
    </Box>
  );
};
